import { AMBIGUOUS_NAMES, CATALOG_NAMES, PARTIAL_PREFIXES, catalogObjectPath } from './ingredientCatalog'
import { normalizeLabel } from './normalizeLabel'
import { isRemovalLabel } from './classifyModifier'

/**
 * El emparejado es EXACTO sobre `normalizeLabel`. Deliberadamente no hay
 * similitud, ni embeddings, ni quitar "extra"/tamaños/plurales. Si
 * `extra de carne mechada` no matchea, la respuesta correcta es agregar el
 * alias — no aflojar el matcher. Un falso positivo le pone al cliente la foto
 * de algo que no va a recibir.
 *
 * `normalizeLabel` e `isRemovalLabel` se reusan a propósito: son los de todo
 * el flujo guiado. Una segunda copia acá se desincronizaría y "Sin Cebolla"
 * pasaría a significar cosas distintas en dos partes del mismo modal.
 *
 * URL pública CRUDA de la foto del catálogo para esta opción, o null.
 *
 * Devuelve la URL sin transformar a propósito: cada superficie le aplica su
 * propio `menuPhotoUrl(url, ancho)` (320 en kiosko, 160 en microsite).
 *
 * Recibe el nombre CANÓNICO (`option.name`), nunca el traducido: el catálogo
 * está en español y un cliente en inglés perdería todas las fotos en silencio.
 *
 * `groupName` es OPCIONAL y solo se usa para desambiguar los nombres de
 * AMBIGUOUS_NAMES (`pollo`, hoy el único). Es el nombre CRUDO del grupo al
 * que pertenece la opción (`modifier_groups.name`, nunca el display/traducido
 * — mismo criterio que `rawName`). Sin `groupName`, o si ninguna regla de
 * contexto matchea, un nombre ambiguo resuelve `null` — nunca adivina. Un
 * nombre NO ambiguo (`pollo mechado`, `carne mechada`, …) ignora `groupName`
 * por completo y resuelve exactamente igual con o sin contexto.
 *
 * Sin `NEXT_PUBLIC_SUPABASE_URL` devuelve null en vez de armar una URL rota:
 * en un preview mal configurado preferimos cero fotos a cuadros rotos.
 */
export function resolveOptionPhoto(rawName: string, groupName?: string | null): string | null {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL
  if (!base) return null

  const normalized = normalizeLabel(rawName)
  if (!normalized) return null
  if (isRemovalLabel(rawName)) return null
  if (PARTIAL_PREFIXES.some((p) => normalized.startsWith(p))) return null

  // `Object.hasOwn` a propósito en las dos búsquedas: tanto `CATALOG_NAMES[x]`
  // como `AMBIGUOUS_NAMES[x]` a secas recorren el prototipo. Una opción
  // llamada "constructor" o "__proto__" devolvería un valor truthy que no es
  // lo esperado (una función o el propio objeto). No es alcanzable con
  // nombres reales de menú, pero el tipado `Record<string, ...>` lo oculta —
  // el test 'nombres del prototipo no cuelan' lo ancla.
  if (Object.hasOwn(AMBIGUOUS_NAMES, normalized)) {
    if (!groupName) return null
    const rules = AMBIGUOUS_NAMES[normalized]
    const rule = rules.find((r) => r.pattern.test(groupName))
    if (!rule) return null
    return `${base}/storage/v1/object/public/menu-images/${catalogObjectPath(rule.slug)}`
  }

  if (!Object.hasOwn(CATALOG_NAMES, normalized)) return null
  const slug = CATALOG_NAMES[normalized]

  return `${base}/storage/v1/object/public/menu-images/${catalogObjectPath(slug)}`
}

/**
 * Lo que el dueño decidió para la foto de UNA opción (Menú → Modificadores).
 * Columnas de `modifier_options`; las dos son opcionales porque las superficies
 * toleran la migración sin aplicar (lib/menu/optionPhotoColumns.ts) y entonces
 * no llegan.
 */
export type OptionPhotoChoice = {
  /** Foto propia subida por el dueño. Null/ausente = la del catálogo. */
  image_url?: string | null
  /** true = sin foto, aunque el catálogo tenga una predeterminada. */
  hide_photo?: boolean | null
}

/**
 * La foto de una opción respetando lo que eligió el dueño, en este orden:
 *   1. `hide_photo` → null (la quitó a propósito).
 *   2. `image_url`  → su foto, tal cual.
 *   3. si no        → la predeterminada del catálogo (`resolveOptionPhoto`).
 *
 * La foto propia gana incluso donde el catálogo nunca pondría una ("Sin
 * cebolla", nombres ambiguos sin contexto): el dueño la subió para ESA opción,
 * así que no hay emparejado que pueda equivocarse.
 */
export function resolveOptionPhotoFor(
  option: { name: string } & OptionPhotoChoice,
  groupName?: string | null,
): string | null {
  if (option.hide_photo === true) return null
  const own = option.image_url?.trim()
  if (own) return own
  return resolveOptionPhoto(option.name, groupName)
}

/**
 * Lo que decide el grupo (Menú → Modificadores → «Mostrar con fotos»). Llega en
 * `false` también cuando el LOCAL apagó las fotos: se pliega al cargar el menú
 * (lib/menu/modifierPhotosSetting.ts). Ausente = encendido.
 */
export type GroupPhotoChoice = { name: string; show_photos?: boolean | null }

/**
 * La foto que pinta una opción DENTRO de su grupo: nada si el grupo (o el local)
 * apagó las fotos; si no, `resolveAvailableOptionPhoto`. Es el `photoOf` de las
 * cuatro superficies: con todas las opciones en null, `groupShowsPhotos` deja el
 * grupo en chips de texto sin ninguna regla aparte.
 */
export function resolveGroupOptionPhoto(
  option: { name: string; is_available?: boolean | null } & OptionPhotoChoice,
  group: GroupPhotoChoice,
): string | null {
  if (group.show_photos === false) return null
  return resolveAvailableOptionPhoto(option, group.name)
}

/**
 * Igual que `resolveOptionPhotoFor`, más la regla de disponibilidad: una opción
 * agotada NO lleva foto. Se muestra apagada y tachada con "Agotado", y una
 * foto apetitosa de algo que hoy no hay es peor que ninguna.
 *
 * Lo consumen los dos renderers CLÁSICOS. Los guiados no lo necesitan: su
 * equivalente ya está resuelto en `toExtraOption` (guidedSteps.ts).
 */
export function resolveAvailableOptionPhoto(
  option: { name: string; is_available?: boolean | null } & OptionPhotoChoice,
  groupName?: string | null,
): string | null {
  if (option.is_available === false) return null
  return resolveOptionPhotoFor(option, groupName)
}
